import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Form, Input, Button, Alert, Typography } from "antd";
import type { FormInstance } from "antd";
import { AppLayout } from "components/AppLayout";
import { AppLogo } from "components/AppLogo";
import styled from "styled-components";
import { h1 } from "common/styles";
import { variables } from "common/variables";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import {
  generateOtp,
  verifyOtp,
  resetPassword,
  setMobile,
  resetFlow,
} from "store/app/forgotPasswordSlice";
import { routes } from "common/routes";
import { mobileFormValidator } from "common/utils";
import { useDocumentTitle } from "hooks/usePageTitle";

const { Text, Link } = Typography;

const RESEND_COUNTDOWN_SECONDS = 30;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
`;

const StyledRow = styled(Row)`
  height: 100%;
`;

const StyledH1 = styled.h1`
  ${h1}
`;

const inputStyle: React.CSSProperties = {
  borderRadius: "10px",
  height: "60px",
};

const buttonStyle: React.CSSProperties = {
  borderRadius: "10px",
  height: "60px",
  marginTop: "10px",
};

export const ForgotPasswordPage = () => {
  useDocumentTitle("Forgot Password");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { step, mobile, otpAuthToken, status, error } = useAppSelector(
    (state) => state.forgotPassword
  );

  const [countdown, setCountdown] = useState(0);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [otpForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  useEffect(() => {
    dispatch(resetFlow());
    return () => {
      dispatch(resetFlow());
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    };
  }, [dispatch]);

  const startResendCountdown = () => {
    setCountdown(RESEND_COUNTDOWN_SECONDS);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    countdownTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleGetOtp = (values: { mobile: string }) => {
    dispatch(setMobile(values.mobile));
    dispatch(generateOtp(`+91${values.mobile}`)).then((result) => {
      if (generateOtp.fulfilled.match(result)) {
        startResendCountdown();
      }
    });
  };

  const handleResendOtp = () => {
    dispatch(generateOtp(`+91${mobile}`)).then((result) => {
      if (generateOtp.fulfilled.match(result)) {
        startResendCountdown();
        otpForm.resetFields();
      }
    });
  };

  const handleVerifyOtp = (values: { otp: string }) => {
    dispatch(verifyOtp({ mobile: `+91${mobile}`, otp: values.otp }));
  };

  const handleResetPassword = (values: { newPassword: string }) => {
    dispatch(resetPassword({ newPassword: values.newPassword, authToken: otpAuthToken })).then(
      (result) => {
        if (resetPassword.fulfilled.match(result)) {
          navigate(`/${routes.LOGIN}`, { replace: true });
        }
      }
    );
  };

  const isLoading = status === "loading";

  return (
    <AppLayout style={{ padding: 0 }}>
      <StyledRow justify="center">
        <Col
          md={12}
          xs={24}
          style={{ backgroundColor: variables.wmBackgroundColorLight1 }}
        >
          <Row
            style={{
              display: "flex",
              height: "100%",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Col style={{ width: "70%" }}>
              <FormContainer>
                <AppLogo />

                {step === 1 && (
                  <Step1EnterMobile
                    isLoading={isLoading}
                    error={error}
                    hasError={status === "error"}
                    onSubmit={handleGetOtp}
                    onBackToLogin={() => navigate(`/${routes.LOGIN}`)}
                  />
                )}

                {step === 2 && (
                  <Step2VerifyOtp
                    mobile={mobile}
                    form={otpForm}
                    isLoading={isLoading}
                    error={error}
                    hasError={status === "error"}
                    countdown={countdown}
                    onSubmit={handleVerifyOtp}
                    onResend={handleResendOtp}
                    onBackToLogin={() => navigate(`/${routes.LOGIN}`)}
                  />
                )}

                {step === 3 && (
                  <Step3ResetPassword
                    form={passwordForm}
                    isLoading={isLoading}
                    error={error}
                    hasError={status === "error"}
                    onSubmit={handleResetPassword}
                  />
                )}
              </FormContainer>
            </Col>
          </Row>
        </Col>
        <Col md={12} xs={0}></Col>
      </StyledRow>
    </AppLayout>
  );
};

// ─── Step sub-components ────────────────────────────────────────────────────

interface StepErrorProps {
  hasError: boolean;
  error: string;
}

const StepError = ({ hasError, error }: StepErrorProps) =>
  hasError ? (
    <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />
  ) : null;

interface Step1Props {
  isLoading: boolean;
  error: string;
  hasError: boolean;
  onSubmit: (values: { mobile: string }) => void;
  onBackToLogin: () => void;
}

const Step1EnterMobile = ({
  isLoading,
  error,
  hasError,
  onSubmit,
  onBackToLogin,
}: Step1Props) => (
  <>
    <StyledH1>Forgot Password</StyledH1>
    <Text type="secondary" style={{ marginBottom: 24, display: "block" }}>
      Enter your registered mobile number to receive an OTP.
    </Text>
    <StepError hasError={hasError} error={error} />
    <Form onFinish={onSubmit}>
      <Form.Item
        name="mobile"
        rules={[
          { required: true, message: "Please input your mobile number!" },
          {
            validator: (_, value) => mobileFormValidator(value),
            validateTrigger: "onFinish",
          },
        ]}
      >
        <Input
          placeholder="Mobile Number"
          size="large"
          maxLength={10}
          style={inputStyle}
        />
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          block
          size="large"
          style={buttonStyle}
          loading={isLoading}
          disabled={isLoading}
        >
          Get OTP
        </Button>
      </Form.Item>
    </Form>
    <div style={{ textAlign: "center", marginTop: 8 }}>
      <Link onClick={onBackToLogin}>Back to Login</Link>
    </div>
  </>
);

interface Step2Props {
  mobile: string;
  form: FormInstance<any>;
  isLoading: boolean;
  error: string;
  hasError: boolean;
  countdown: number;
  onSubmit: (values: { otp: string }) => void;
  onResend: () => void;
  onBackToLogin: () => void;
}

const Step2VerifyOtp = ({
  mobile,
  form,
  isLoading,
  error,
  hasError,
  countdown,
  onSubmit,
  onResend,
  onBackToLogin,
}: Step2Props) => (
  <>
    <StyledH1>Verify OTP</StyledH1>
    <Text type="secondary" style={{ marginBottom: 8, display: "block" }}>
      A 6-digit OTP has been sent to +91{mobile}
    </Text>
    <StepError hasError={hasError} error={error} />
    <Form form={form} onFinish={onSubmit}>
      <Form.Item
        name="otp"
        rules={[
          { required: true, message: "Please enter the OTP!" },
          { pattern: /^\d{6}$/, message: "OTP must be exactly 6 digits!" },
        ]}
      >
        <Input
          placeholder="Enter 6-digit OTP"
          size="large"
          maxLength={6}
          style={{ ...inputStyle, letterSpacing: "12px", textAlign: "center" }}
        />
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          block
          size="large"
          style={buttonStyle}
          loading={isLoading}
          disabled={isLoading}
        >
          Verify OTP
        </Button>
      </Form.Item>
    </Form>
    <div style={{ textAlign: "center", marginTop: 8 }}>
      {countdown > 0 ? (
        <Text type="secondary">Resend OTP in {countdown}s</Text>
      ) : (
        <Link onClick={isLoading ? undefined : onResend}>Resend OTP</Link>
      )}
    </div>
    <div style={{ textAlign: "center", marginTop: 12 }}>
      <Link onClick={onBackToLogin}>Back to Login</Link>
    </div>
  </>
);

interface Step3Props {
  form: FormInstance<any>;
  isLoading: boolean;
  error: string;
  hasError: boolean;
  onSubmit: (values: { newPassword: string; confirmPassword: string }) => void;
}

const Step3ResetPassword = ({
  form,
  isLoading,
  error,
  hasError,
  onSubmit,
}: Step3Props) => (
  <>
    <StyledH1>Reset Password</StyledH1>
    <Text type="secondary" style={{ marginBottom: 24, display: "block" }}>
      Enter and confirm your new password.
    </Text>
    <StepError hasError={hasError} error={error} />
    <Form form={form} onFinish={onSubmit}>
      <Form.Item
        name="newPassword"
        rules={[
          { required: true, message: "Please enter a new password!" },
          { min: 6, message: "Password must be at least 6 characters!" },
        ]}
      >
        <Input.Password
          placeholder="New Password"
          size="large"
          style={inputStyle}
        />
      </Form.Item>
      <Form.Item
        name="confirmPassword"
        dependencies={["newPassword"]}
        rules={[
          { required: true, message: "Please confirm your password!" },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("newPassword") === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error("Passwords do not match!"));
            },
            validateTrigger: "onChange",
          }),
        ]}
      >
        <Input.Password
          placeholder="Confirm Password"
          size="large"
          style={inputStyle}
        />
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          block
          size="large"
          style={buttonStyle}
          loading={isLoading}
          disabled={isLoading}
        >
          Reset Password
        </Button>
      </Form.Item>
    </Form>
  </>
);
